import React, { useMemo, useState, useEffect } from "react";
import ReusableTable from "../components/Table/ReusableTable";
import { CheckCircle2, XCircle, Search, Filter, AlertTriangle, Loader2, Check, ArrowRight, TrendingUp, Users, Zap, Briefcase, FileText } from "lucide-react";
import Button from "../components/Button/Button";
import { Tooltip } from "react-tooltip";
import { post } from "../utils/api";
import "react-tooltip/dist/react-tooltip.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const KpiCard = ({ title, value, icon: Icon, trend, color = "indigo" }) => (
  <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group animate-in fade-in slide-in-from-bottom-3 duration-500">
    <div className="flex justify-between items-start mb-4">
      <div className={`p-3 rounded-xl bg-${color}-50 text-${color}-500 group-hover:scale-110 transition-transform`}>
        <Icon size={20} />
      </div>
      {trend && (
        <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-full">
          <TrendingUp size={10} className="mr-0.5" /> {trend}
        </span>
      )}
    </div>
    <div>
      <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1">{title}</p>
      <h3 className="text-xl font-bold text-gray-800 tracking-tight">{value}</h3>
    </div>
  </div>
);

const TransactionFeeMismatch = () => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    distributor_name: "",
    month: "",
  });
  const [selectedRowIds, setSelectedRowIds] = useState({});

  const userId = localStorage.getItem("user_details")
    ? JSON.parse(localStorage.getItem("user_details")).id
    : null;

  const monthNames = {
    "01": "January", "02": "February", "03": "March", "04": "April",
    "05": "May", "06": "June", "07": "July", "08": "August",
    "09": "September", 10: "October", 11: "November", 12: "December",
  };

  const monthOptions = Object.entries(monthNames).map(([value, label]) => ({
    value, label,
  }));

  const fetchTableData = async () => {
    setLoading(true);
    try {
      const transformedFilters = {
        distributor_name: filters.distributor_name,
        month: monthNames[filters.month] || "",
      };
      const res = await post("/transaction/fetchFeeMismatchDetails", transformedFilters);
      if (res.data.Success) {
        setTableData(res.data.Data || []);
        setSelectedRowIds({});
      } else {
        setTableData([]);
        toast.warning(res.data.Message || "No records found.");
        setSelectedRowIds({});
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error fetching data");
      setTableData([]);
      setSelectedRowIds({});
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const uniqueDistributors = new Set(tableData.map(d => d.distributor_name)).size;
    const selectedCount = Object.keys(selectedRowIds).length;
    return {
      mismatches: tableData.length,
      distributors: uniqueDistributors,
      selected: selectedCount
    };
  }, [tableData, selectedRowIds]);

  useEffect(() => {
    fetchTableData();
  }, []);

  const handleInputChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const getSelectedRows = () =>
    Object.keys(selectedRowIds)
      .map((rowIndex) => tableData[parseInt(rowIndex, 10)])
      .filter(Boolean);

  const handleApproveAll = async () => {
    const selectedRows = getSelectedRows();
    if (selectedRows.length === 0) {
      toast.warning("Please select transactions to approve.");
      return;
    }
    const payload = {
      user_Id: userId,
      transaction: selectedRows.map((row) => ({
        transaction_id: row.transaction_id,
        transaction_details_id: row.transaction_detail_id,
      })),
    };

    try {
      const res = await post("/transaction/approveTransactionDetails", payload);
      if (res.data.Success) {
        toast.success("Transactions approved successfully.");
        fetchTableData();
      } else {
        toast.error(res.data.Message || "Failed to approve transactions.");
      }
    } catch (error) {
      toast.error("Error occurred during approval.");
    }
  };

  const handleRejectAll = async () => {
    const selectedRows = getSelectedRows();
    if (selectedRows.length === 0) {
      toast.warning("Please select transactions to reject.");
      return;
    }
    const payload = {
      user_Id: userId,
      transaction: selectedRows.map((row) => ({
        transaction_id: row.transaction_id,
        transaction_details_id: row.transaction_detail_id,
      })),
    };

    try {
      const res = await post("/transaction/rejectTransactionDetails", payload);
      if (res.data.Success) {
        toast.success("Transactions rejected successfully.");
        fetchTableData();
      } else {
        toast.error(res.data.Message || "Failed to reject transactions.");
      }
    } catch (error) {
      toast.error("Error occurred during rejection.");
    }
  };

  const columns = useMemo(() => {
    const checkboxColumn = {
      id: "selection",
      Header: () => (
        <div className="flex justify-center items-center">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
            checked={tableData.length > 0 && Object.keys(selectedRowIds).length === tableData.length}
            onChange={(e) => {
              if (e.target.checked) {
                const allSelected = {};
                tableData.forEach((_, idx) => { allSelected[idx] = true; });
                setSelectedRowIds(allSelected);
              } else {
                setSelectedRowIds({});
              }
            }}
          />
        </div>
      ),
      Cell: ({ row }) => (
        <div className="flex justify-center items-center">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
            checked={!!selectedRowIds[row.index]}
            onChange={(e) => {
              setSelectedRowIds((prev) => {
                const newSelected = { ...prev };
                if (e.target.checked) newSelected[row.index] = true;
                else delete newSelected[row.index];
                return newSelected;
              });
            }}
          />
        </div>
      ),
      size: 60,
    };

    const renderWithNA = (accessor, isFee = false) => ({
      Header: accessor.replace(/_/g, " ").toUpperCase(),
      accessor,
      Cell: ({ value }) => {
        if (value == null || value === "") return <span className="text-gray-300 font-medium italic">N/A</span>;
        if (isFee) return <span className="font-bold text-gray-700">{value}%</span>;
        return <span className="text-gray-700 font-medium">{value}</span>;
      },
    });

    return [
      checkboxColumn,
      {
        Header: "DISTRIBUTOR",
        accessor: "distributor_name",
        Cell: ({ value }) => <span className="font-black text-gray-900 uppercase tracking-tight break-words line-clamp-2">{value || "N/A"}</span>,
        size: 200,
      },
      { Header: "MONTH", accessor: "month", size: 100 },
      { Header: "PROD CODE", accessor: "product_code", size: 120 },
      {
        Header: "TRX FEES (P/U/T)",
        id: "trx_fees",
        Cell: ({ row }) => {
          const { placement_fee_per, upfront_fee_per, trail_per } = row.original;
          return (
            <div className="flex items-center gap-1.5 font-bold text-[10px]">
              <span className="text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded" title="Placement">{placement_fee_per || 0}%</span>
              <span className="text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded" title="Upfront">{upfront_fee_per || 0}%</span>
              <span className="text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" title="Trail">{trail_per || 0}%</span>
            </div>
          )
        },
        size: 160
      },
      {
        Header: "MS FEES (P/U/T)",
        id: "ms_fees",
        Cell: ({ row }) => {
          const { ms_placement_fee_per, ms_upload_fee_per, ms_trail_fee_per } = row.original;
          return (
            <div className="flex items-center gap-1.5 font-bold text-[10px]">
              <span className="text-indigo-400 border border-indigo-100 px-1.5 py-0.5 rounded" title="MS Placement">{ms_placement_fee_per || 0}%</span>
              <span className="text-emerald-400 border border-emerald-100 px-1.5 py-0.5 rounded" title="MS Upfront">{ms_upload_fee_per || 0}%</span>
              <span className="text-amber-400 border border-amber-100 px-1.5 py-0.5 rounded" title="MS Trail">{ms_trail_fee_per || 0}%</span>
            </div>
          )
        },
        size: 160
      },
      {
        Header: "STATUS REMARK",
        accessor: "check_remarks",
        Cell: ({ value }) => (
          <div className={`p-2 rounded-lg text-xs font-bold flex items-center gap-2 border ${value?.toLowerCase().includes("mismatch") ? "bg-red-50 text-red-700 border-red-100" : "bg-gray-50 text-gray-600 border-gray-100"}`}>
            <AlertTriangle size={12} />
            {value || "No remarks"}
          </div>
        ),
        size: 250
      },
      {
        Header: "ACTION",
        id: "action",
        size: 120,
        Cell: ({ row }) => (
          <div className="flex gap-2 justify-center">
            <button
              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
              onClick={async () => {
                const payload = {
                  user_Id: userId,
                  transaction: [{ transaction_id: row.original.transaction_id, transaction_details_id: row.original.transaction_detail_id }],
                };
                const res = await post("/transaction/approveTransactionDetails", payload);
                if (res.data.Success) { toast.success("Approved!"); fetchTableData(); }
                else toast.error("Error Approving");
              }}
              title="Quick Approve"
            >
              <CheckCircle2 size={18} />
            </button>
            <button
              className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
              onClick={async () => {
                const payload = {
                  user_Id: userId,
                  transaction: [{ transaction_id: row.original.transaction_id, transaction_details_id: row.original.transaction_detail_id }],
                };
                const res = await post("/transaction/rejectTransactionDetails", payload);
                if (res.data.Success) { toast.success("Rejected!"); fetchTableData(); }
                else toast.error("Error Rejecting");
              }}
              title="Quick Reject"
            >
              <XCircle size={18} />
            </button>
          </div>
        ),
      },
    ];
  }, [userId, selectedRowIds, tableData]);

  return (
    <div className="w-full max-w-screen-xl mx-auto p-6 h-full min-h-screen bg-gray-50/30 space-y-8 animate-in fade-in duration-700">
      <ToastContainer position="top-right" autoClose={2000} hideProgressBar theme="colored" />

      {/* Premium Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-amber-600 flex items-center justify-center text-white shadow-lg shadow-amber-100 border border-amber-500">
              <AlertTriangle size={24} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800 tracking-tight leading-tight uppercase">Mismatch Resolution</h1>
              <p className="text-[11px] font-medium text-gray-400 flex items-center gap-2 ml-1">
                Reconciliation Hub <span className="w-1 h-1 rounded-full bg-gray-300" /> Audit Fee Discrepancies
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleRejectAll}
            className="flex items-center gap-2 bg-white hover:bg-rose-50 text-rose-600 border border-rose-100 h-[46px] px-6 rounded-xl font-bold text-sm shadow-sm transition-all active:scale-95"
          >
            <XCircle size={18} />
            Reject Selected
          </button>
          <button
            onClick={handleApproveAll}
            className="flex items-center gap-2 bg-[#10B981] hover:bg-[#059669] text-white h-[46px] px-8 rounded-xl font-bold text-sm shadow-lg shadow-emerald-100 transition-all active:scale-95"
          >
            <CheckCircle2 size={18} />
            Approve & Sync
          </button>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-6 items-end">
        <div className="flex-1 w-full grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col gap-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Search Distributor</label>
            <div className="relative group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-amber-500 transition-colors w-4 h-4" />
              <input
                type="text"
                name="distributor_name"
                value={filters.distributor_name}
                onChange={handleInputChange}
                placeholder="Enter distributor name..."
                className="w-full pl-11 pr-4 h-[48px] border border-gray-100 rounded-xl text-sm font-bold text-gray-700 bg-gray-50/50 hover:border-amber-200 focus:bg-white focus:ring-2 focus:ring-amber-100 focus:border-amber-500 outline-none transition-all"
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Filter By Month</label>
            <div className="relative group">
              <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-amber-500 transition-colors w-4 h-4 text-gray-400" />
              <select
                name="month"
                value={filters.month}
                onChange={handleInputChange}
                className="w-full pl-11 pr-4 h-[48px] border border-gray-100 rounded-xl text-sm font-bold text-gray-700 bg-gray-50/50 hover:border-amber-200 focus:bg-white appearance-none focus:ring-2 focus:ring-amber-100 focus:border-amber-500 outline-none transition-all"
              >
                <option value="">All Statement Months</option>
                {monthOptions.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
          </div>
        </div>
        <button
          onClick={fetchTableData}
          className="h-[48px] px-10 bg-gray-900 text-white rounded-xl font-bold text-sm hover:bg-black shadow-lg shadow-gray-100 transition-all active:scale-95 flex items-center gap-2"
        >
          <Search size={18} />
          Execute Search
        </button>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-bottom-8 duration-1000">
        <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.5)] animate-pulse" />
            <span className="font-black uppercase tracking-widest text-[10px] text-gray-500">Conflict Registry: {tableData.length}</span>
          </div>
          {Object.keys(selectedRowIds).length > 0 && (
            <div className="text-[10px] font-black uppercase tracking-widest text-amber-600 bg-amber-50 px-4 py-1.5 rounded-full border border-amber-100 animate-pulse">
              {Object.keys(selectedRowIds).length} Conflict Sessions Selected
            </div>
          )}
        </div>

        <div className="bg-white">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4 text-gray-300">
              <Loader2 size={48} className="animate-spin text-amber-500" />
              <span className="font-black text-sm tracking-[0.2em] uppercase">Processing Data...</span>
            </div>
          ) : tableData.length > 0 ? (
            <div className="p-1">
              <ReusableTable columns={columns} data={tableData} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-32 text-gray-400 gap-2">
              <Check className="w-12 h-12 text-gray-200" />
              <p className="font-bold text-sm italic">All fees are currently reconciled. No mismatches found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionFeeMismatch;
